<script setup>
import { computed, defineEmits, defineProps, onMounted, reactive, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import api from '@/api'
import { getErrors } from '@/errors'
import { useFormProvider } from '@/components/common/forms/composables'
import FormField from '@/components/common/forms/FormField'
import FormLabel from '@/components/common/forms/FormLabel'
import FormInput from '@/components/common/forms/FormInput'
import FormSelect from '@/components/common/forms/FormSelect'
import FormFieldError from '@/components/common/forms/FormFieldError'

import AgreementDialog from '@/components/journal/parent/tax-deduction/AgreementDialog'

defineProps(['user'])
const emit = defineEmits(['success', 'close'])

const toast = useToast()

const isSchemaPending = ref(false)
const schema = ref(null)

const fetchSchema = () => {
  if (isSchemaPending.value) return

  isSchemaPending.value = true

  api.options('/legal/tax-deduction/report-requests/')
    .then((response) => {
      schema.value = response.data.actions.POST
    }, error => {
      toast.error(getErrors(error).detail)
    })
    .then(() => {
      isSchemaPending.value = false
    })
}

onMounted(() => {
  fetchSchema()
})

const isPayerSuggestionsPending = ref(false)
const payerSuggestions = ref(null)

const fetchPayerSuggestions = () => {
  if (isPayerSuggestionsPending.value) return

  isPayerSuggestionsPending.value = true

  api.get('/legal/tax-deduction/payer-suggestions/')
    .then((response) => {
      payerSuggestions.value = response.data
    }, error => {
      toast.error(getErrors(error).detail)
    })
    .then(() => {
      isPayerSuggestionsPending.value = false
    })
}

onMounted(() => {
  fetchPayerSuggestions()
})

const isChildrenPending = ref(false)
const children = ref(null)

const isSingleChild = computed(() => children.value && children.value.length === 1)

const fetchChildren = () => {
  if (isChildrenPending.value) return

  isChildrenPending.value = true

  api.get('/legal/tax-deduction/children/')
    .then((response) => {
      children.value = response.data
    }, error => {
      toast.error(getErrors(error).detail)
    })
    .then(() => {
      isChildrenPending.value = false
    })
}

onMounted(() => {
  fetchChildren()
})

watch(isSingleChild, (_isSingleChild) => {
  if (_isSingleChild) {
    values.child = children.value[0].id
  }
})

const isAgreementTokenPending = ref(false)
const agreementToken = ref(null)

const fetchAgreementToken = () => {
  if (isAgreementTokenPending.value) return

  isAgreementTokenPending.value = true

  api.post('/legal/obtain-agreement-token/')
    .then((response) => {
      agreementToken.value = response.data
    }, error => {
      toast.error(getErrors(error).detail)
    })
    .then(() => {
      isAgreementTokenPending.value = false
    })
}

onMounted(() => {
  fetchAgreementToken()
})

const agreed = ref(false)

const isPending = computed(() =>
  [
    isSchemaPending,
    isChildrenPending,
    isPayerSuggestionsPending,
    isAgreementTokenPending
  ].some((_ref) => _ref.value)
)
const isReady = computed(() =>
  [
    schema,
    children,
    payerSuggestions,
    agreementToken
  ].every((_ref) => !!_ref.value)
)

const isSubmitting = ref(false)
const errors = ref({})

const values = reactive({
  payer_last_name: '',
  payer_first_name: '',
  payer_middle_name: '',
  payer_inn: '',
  payer_date_of_birth: '',
  payer_document_code: '21',
  payer_document_number: '',
  payer_document_date: '',
  child: null,
  student_last_name: '',
  student_first_name: '',
  student_middle_name: '',
  student_inn: '',
  student_date_of_birth: '',
  student_document_code: '03',
  student_document_number: '',
  student_document_date: '',
  year: ''
})

const submit = () => {
  if (!agreed.value) return
  if (isSubmitting.value) return

  isSubmitting.value = true
  errors.value = {}

  api.post('/legal/tax-deduction/report-requests/', {
    ...values,
    agreement_token: agreementToken.value.key
  })
    .then(() => {
      close()
      toast.success('Запрос отправлен')
      emit('success')
    }, error => {
      errors.value = getErrors(error)
    })
    .then(() => {
      isSubmitting.value = false
    })
}

const applySuggestion = (suggestion) => {
  Object.entries(suggestion).forEach(([name, value]) => {
    if (value !== null) {
      values[name] = value
    }
  })
}

watch(() => values.child, (childID) => {
  const child = children.value.find((child) => child.id === childID)

  applySuggestion(child.suggestion)
})

const isAgreementDialogVisible = ref(false)
const isPolicyDialogVisible = ref(false)

const close = () => {
  emit('close')
}

const maybeClose = () => {
  if (confirm('Если вы закроете форму, введённые данные будут утеряны. Вы уверены?')) {
    close()
  }
}

useFormProvider({ schema, values, errors })
</script>

<template>
  <div class="dialog-wrapper">
    <div class="dialog-overlay"></div>
    <div class="dialog-box">
      <div class="dialog-header">
        <div class="dialog-header-text">
          Справка для получения налогового вычета
        </div>
        <BaseCloseButton @click="maybeClose" />
      </div>
      <div class="dialog-content">
        <form @submit.prevent="submit">
          <BaseSpinner v-if="isPending || isSubmitting" size="50%" :withBackground="true"/>
          <template v-if="isReady">
            <div class="dialog-form-wrapper">
              <fieldset class="dialog-form-fieldset">
                <div class="dialog-form-fieldset-header">
                  Данные физического лица (его супруга/супруги),
                  оплатившего образовательные услуги
                </div>
                <div class="dialog-form-separator"></div>
                <div class="payer-suggestions-block">
                  <div>Заполнить автоматически:</div>
                  <ul class="payer-suggestions">
                    <li v-for="(suggestion, i) in payerSuggestions" :key="i">
                      <a
                        v-text="`${suggestion.payer_last_name} ${suggestion.payer_first_name}`"
                        @click="applySuggestion(suggestion)"
                        class="auxiliary-link"
                      ></a>
                    </li>
                  </ul>
                </div>
                <FormField fieldName="payer_last_name">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Фамилия</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="payer_first_name">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Имя</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="payer_middle_name">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Отчество</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="payer_inn">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">ИНН</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="payer_date_of_birth">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Дата рождения</FormLabel>
                    <FormInput type="date" class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <div class="dialog-form-separator"></div>
                <FormField fieldName="payer_document_code">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Вид документа</FormLabel>
                    <FormSelect class="form-input dialog-form-input">
                      <option value="21">Паспорт гражданина РФ</option>
                      <option value="03">Свидетельство о рождении</option>
                    </FormSelect>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="payer_document_number">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Серия и номер</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="payer_document_date">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Дата выдачи</FormLabel>
                    <FormInput type="date" class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
              </fieldset>
              <fieldset class="dialog-form-fieldset">
                <div class="dialog-form-fieldset-header">
                  Данные физического лица,
                  которому оказаны образовательные услуги
                </div>
                <div class="dialog-form-separator"></div>
                <FormField fieldName="child" v-if="!isSingleChild">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label"></FormLabel>
                    <FormSelect class="form-input dialog-form-input">
                      <option
                        v-for="child in children"
                        v-text="child.first_name"
                        :value="child.id"
                        :key="child.id"
                      ></option>
                    </FormSelect>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_last_name">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Фамилия</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_first_name">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Имя</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_middle_name">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Отчество</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_inn">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">ИНН</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_date_of_birth">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Дата рождения</FormLabel>
                    <FormInput type="date" class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <div class="dialog-form-separator"></div>
                <FormField fieldName="student_document_code">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Вид документа</FormLabel>
                    <FormSelect class="form-input dialog-form-input">
                      <option value="21">Паспорт гражданина РФ</option>
                      <option value="03">Свидетельство о рождении</option>
                    </FormSelect>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_document_number">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Серия и номер</FormLabel>
                    <FormInput class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
                <FormField fieldName="student_document_date">
                  <div class="dialog-form-group">
                    <FormLabel class="dialog-form-label">Дата выдачи</FormLabel>
                    <FormInput type="date" class="form-input dialog-form-input"/>
                  </div>
                  <FormFieldError class="dialog-form-alert" />
                </FormField>
              </fieldset>
              <FormField fieldName="year">
                <div class="dialog-form-group">
                  <FormLabel class="dialog-form-label"></FormLabel>
                  <FormInput class="form-input dialog-form-input"/>
                </div>
                <FormFieldError class="dialog-form-alert" />
              </FormField>
              <div class="dialog-form-group">
                <label class="dialog-form-checkbox-label">
                  <input type="checkbox" v-model="agreed" required>
                  <span> Я даю </span>
                  <a
                    class="auxiliary-link"
                    @click.prevent="isAgreementDialogVisible = true"
                  >согласие на обработку персональных данных</a><span>.</span>
                </label>
              </div>
              <div class="dialog-form-help-text">
                <a
                  class="auxiliary-link"
                  @click="isPolicyDialogVisible = true"
                >Политика конфиденциальности</a>
              </div>
            </div>
            <BaseFormError :errors="errors.non_field_errors" />
            <BaseFormError :errors="errors.detail" />
            <div class="dialog-buttons">
              <button type="submit" class="button no-margin" :disabled="isSubmitting">Отправить</button>
            </div>
          </template>
        </form>
      </div>
    </div>
  </div>
  <AgreementDialog
    v-if="isAgreementDialogVisible"
    @close="isAgreementDialogVisible = false"
    header="Согласие на обработку персональных данных"
    :text="agreementToken.agreement_text"
  />
  <AgreementDialog
    v-if="isPolicyDialogVisible"
    @close="isPolicyDialogVisible = false"
    header="Политика в отношении обработки персональных данных"
    :text="agreementToken.policy_text"
  />
</template>

<style scoped>
.dialog-box {
  width: 100%;

  display: flex;
  flex-direction: column;
}

.dialog-content {
  min-height: 200px;
  overflow: auto;
}

.dialog-form-wrapper {
  padding: 0 1em;

  display: flex;
  flex-direction: column;
  gap: 1em;
}

.dialog-form-fieldset {
  border-radius: 0.5em;
  border: none;

  margin: 0 -1em;
  padding: 1em;

  background-color: var(--light-gray);

  display: flex;
  flex-direction: column;
  gap: 1em;
}

.dialog-form-fieldset-header {
  font-weight: 500;
}

.dialog-form-separator {
  border-bottom: 1px var(--medium-gray) dotted;
}

.dialog-form-group {
  display: flex;
  align-items: center;
}

.dialog-form-label {
  width: 30%;
  padding-right: 1em;
  text-align: right;
  font-weight: 500;
}

.dialog-form-label.required::after {
  content: "*";
  color: var(--red);
}

.dialog-form-input {
  color: var(--dark-text);
  width: 100%;
  padding: 0.8em 1em;
  margin: 0;
  text-align: left;
  font-weight: normal;
  border: 1px solid var(--medium-gray);
  background: white;

  flex: 1;
}

.dialog-form-input:focus {
  border: 1px solid var(--dark-gray);
}

.dialog-form-alert {
  margin: -0.7em 0 0 30% !important;
}

.has-errors {
  border-style: dashed;
  border-color: var(--red-darker);
}

.dialog-buttons {
  padding: 0;
  margin: 1.5em 0 0 0;
}

.payer-suggestions-block {
  background-color: var(--light-overlay);
  padding: 1em;
  border-radius: 1em;
}

.payer-suggestions {
  list-style: none;
  padding-left: 0;
}

.auxiliary-link {
  color: var(--dark-gray);
  cursor: pointer;
  text-decoration: underline dotted;
  font-style: italic;
  line-height: 1.5em;
}

.auxiliary-link:hover {
  color: var(--dark-text);
}

@media screen and (max-width: 768px) {
  .dialog-box {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;

    width: auto;
    height: auto;

    margin: 0;
    border-radius: 0;
  }

  .dialog-header {
    border-radius: 0;
  }

  .dialog-content {
    margin-bottom: 0;
  }

  .dialog-form-fieldset {
    gap: 1.3em;
  }

  .dialog-form-group {
    flex-direction: column;
    align-items: stretch;
  }

  .dialog-form-label {
    width: 100%;
    text-align: left;
    margin-bottom: 0.5em;
  }

  .dialog-form-alert {
    margin: 0 0 1em 0 !important;
  }

  .dialog-buttons {
    padding-bottom: 1.5em;
  }
}

@media screen and (min-width: 769px) {
  .dialog-box {
    width: 768px;
    max-height: 95vh;
  }

  .dialog-form-checkbox-label {
    margin: 0.5em 0 0.5em 30%;
  }

  .dialog-form-help-text {
    margin: -0.5em 0 0 30%;
  }
}

@media screen and (max-width: 600px) {
  .dialog-box {
    font-size: 12pt;
  }
}

</style>
